import { pickBy } from "lodash"

import User, { IGetUserDto } from "models/User"
import ApiService from "./ApiService"
import AuthService from "./AuthService"
import { ApiServiceName, ApiLinkEntity, ApiLinkRel, ErrorCode } from "config/constants"
import Language, { ILanguageDto } from "models/Language"
import { IMultipleEntitiesResponse, IApiLink, IMultipleEntities, Guid } from "@/types"
import { updateLocale } from "utils/moment"
import TenantSettings, { IGetTenantSettingsDto } from "models/TenantSettings"
import appInsights from "utils/appInsights"
import CustomError from "models/CustomError"
import TenantContacts, { IGetTenantContactsDto, ITenantContacts } from "models/TenantContacts"
import TermsAndConditions, { IGetTermsAndConditionsDto, IUpdateTermsAndConditions } from "models/TermsAndConditions"
import TenantEmployees, { IGetTenantEmployeesDto, IUpdateTenantEmployeesDto } from "models/TenantEmployees"
import DeliveryTerms, { IGetDeliveryTermsDto } from "models/DeliveryTerms"
import TextBuildingBlock, { IGetTextBuildingBlockDto, ICreateTextBuildingBlockDto, IUpdateTextBuildingBlockDto, IGetTextBuilidngBlockParams } from "models/TextBuildingBlock"
import { hasMorePages } from "./utils"
import Company from "models/Company"
import AccessibleCompany, { IAccessibleCompanyDto } from "models/AccessibleCompany"
import TenantInvoicePositions, { IGetTenantInvoicePositionsDto } from "models/TenantInvoicePositions"
import { ICreateTenantInvoicePositionDto, IUpdateTenantInvoicePositionDto } from "models/TenantInvoicePosition"
import OrderTypes, { IGetOrderTypesDto } from "models/OrderTypes"
import { ICreateOrderTypeDto } from "models/OrderType"
import PaymentTerm, { IGetPaymentTermDto } from "models/PaymentTerm"
import Country, { IGetCountryDto } from "models/Country"
import i18n from "intl/i18n"
import TenantSettingsLanguages, { IGetTenantSettingsLanguagesDto, IUpdateTenantSettingsLanguagesDto } from "models/TenantSettingsLanguages"
import ErrorHandler from "utils/errorHandler"
import DeliveryTerm, { ICreateUpdateDeliveryTermDto, IGetDeliveryTermDto } from "models/DeliveryTerm"
import { prepareTranslations } from "utils/translations"
import UserRoles, { IGetUserRolesItemDto, Role } from "models/UserRoles"
import TenantSettingsGlobalAssetOverview, { IGetTenantSettingsGlobalAssetOverviewDto, IUpdateTenantSettingsGlobalAssetOverviewDto } from "models/TenantSettingsGlobalAssetOverview"
import GlobalAssetOverviewTenant, { IGetGlobalAssetOverviewTenantDto } from "models/GlobalAssetOverviewTenant"

export class UserService {
    #currentUser: User | null = null
    #userLanguages: Language[] | null = null
    #userEmails: { [userId: string]: string } = {}
    #tenantLanguages: Language[] | null = null
    #companies: Company[] | null = null
    #countries: Country[] | null = null
    #countriesLanguage: string | null = null
    #countriesWithInternalCode: Country[] | null = null
    #countriesWithInternalCodeLanguage: string | null = null

    async getCurrentUser(): Promise<User | null> {
        if (this.#currentUser !== null) {
            return this.#currentUser
        }
        try {
            const oidcUser = await AuthService.getOidcUser()
            if (!oidcUser) return null

            const userDto: IGetUserDto = await ApiService.request({
                serviceName: ApiServiceName.user,
                followLink: {
                    entity: ApiLinkEntity.user,
                    rel: ApiLinkRel.self
                }
            })

            if (oidcUser.profile.sub) {
                try {
                    appInsights.initAppInsights({
                        userId: oidcUser.profile.sub,
                        userEmail: userDto.email,
                        tenantName: (oidcUser.profile.company_name as string | undefined) ?? "",

                    })
                } catch (e) {
                    console.error(e)
                }
            }

            const user = new User({
                ...userDto,
                firstName: oidcUser.profile.given_name,
                lastName: oidcUser.profile.family_name,
                name: oidcUser.profile.name,
                companyName: oidcUser.profile.company_name as string | undefined,
                companyId: oidcUser.profile.company_id as string | undefined,
            })

            this.#currentUser = user

            return user
        } catch (e) { }
        return null
    }


    async getUserLanguages(): Promise<Language[]> {
        if (this.#userLanguages !== null) {
            return this.#userLanguages
        }
        try {
            const resp: ILanguageDto[] = await ApiService.request({
                serviceName: ApiServiceName.user,
                followLink: {
                    entity: ApiLinkEntity.userLanguage,
                    rel: ApiLinkRel.all
                }
            })
            const languages = resp.map(dto => new Language(dto))
            this.#userLanguages = languages
            return languages
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError
            })
        }
    }

    async getTenantLanguages(): Promise<Language[]> {
        if (this.#tenantLanguages !== null) {
            return this.#tenantLanguages
        }
        try {
            const resp: ILanguageDto[] = await ApiService.request({
                serviceName: ApiServiceName.user,
                followLink: {
                    entity: ApiLinkEntity.tenantLanguage,
                    rel: ApiLinkRel.all
                }
            })
            const languages = resp.map(dto => new Language(dto))
            this.#tenantLanguages = languages
            return languages
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError
            })
        }
    }

    async setLanguage(linkUpdateUser: IApiLink, language: string): Promise<void> {
        try {
            await ApiService.followLink({
                link: linkUpdateUser,
                data: {
                    language
                }
            })
            await updateLocale(language)
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getUserEmailByLink(link: IApiLink): Promise<string | null> {
        try {
            const data: { mail: string } = await ApiService.followLink({ link })
            return data.mail
        } catch (e) {
        }

        return null
    }

    async getUserEmailsByIds(userIds: Guid[]): Promise<Record<Guid, string>> {
        try {
            if (userIds.length === 0) {
                return {}
            }
            const userIdsToFetch = userIds.filter(id => !this.#userEmails[id])
            if (userIdsToFetch.length === 0) {
                return pickBy(this.#userEmails, (value, key) => userIds.includes(key))
            }
            const resp: { usersMail: { id: Guid, mail: string }[] } = await ApiService.request({
                serviceName: ApiServiceName.user,
                followLink: {
                    entity: ApiLinkEntity.userMail,
                    rel: ApiLinkRel.all,
                },
                data: { userIds: userIdsToFetch },
            })
            const result = resp.usersMail.reduce((acc, { id, mail }) => {
                acc[id] = mail
                return acc
            }, {} as { [userId: string]: string })
            this.#userEmails = { ...this.#userEmails, ...result }
            return {
                ...pickBy(this.#userEmails, (value, key) => userIds.includes(key)),
                ...result,
            }
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getAllUserEmails(): Promise<{ id: Guid, mail: string }[]> {
        try {
            const resp: { id: Guid, mail: string }[] = await ApiService.request({
                serviceName: ApiServiceName.user,
                followLink: {
                    entity: ApiLinkEntity.userMails,
                    rel: ApiLinkRel.all,
                },
            })
            this.#userEmails = {
                ...this.#userEmails,
                ...resp.reduce((acc, { id, mail }) => {
                    acc[id] = mail
                    return acc
                }, {} as { [userId: string]: string })
            }
            return resp
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getTenantSettings(): Promise<TenantSettings | null> {
        try {
            const dto: IGetTenantSettingsDto = await ApiService.request({
                serviceName: ApiServiceName.user,
                followLink: {
                    entity: ApiLinkEntity.tenantSettings,
                    rel: ApiLinkRel.self
                },
            })
            return new TenantSettings(dto)
        } catch (e) { }
        return null
    }

    async getTenantSettingsLanguages(): Promise<TenantSettingsLanguages> {
        try {
            const dto: IGetTenantSettingsLanguagesDto = await ApiService.request({
                serviceName: ApiServiceName.user,
                followLink: {
                    entity: ApiLinkEntity.tenantSettingsLanguage,
                    rel: ApiLinkRel.all
                },
            })
            return new TenantSettingsLanguages(dto)
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async updateTenantSettingsLanguages(link: IApiLink, data: IUpdateTenantSettingsLanguagesDto): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data,
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getTenantContacts(): Promise<TenantContacts> {
        try {
            const dto: IGetTenantContactsDto = await ApiService.request({
                serviceName: ApiServiceName.user,
                followLink: {
                    entity: ApiLinkEntity.tenantSettingsContacts,
                    rel: ApiLinkRel.all,
                },
                includeEtag: true,
            })
            return new TenantContacts(dto)
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async updateTenantContacts(link: IApiLink, data: ITenantContacts, eTag?: string): Promise<TenantContacts> {
        try {
            const dto: IGetTenantContactsDto = await ApiService.followLink({
                link,
                data,
                headers: eTag ? {
                    "If-Match": eTag
                } : undefined
            })

            return new TenantContacts(dto)
        } catch (e) {
            if (e.isAxiosError && e.response.status === 409) {
                throw new CustomError({
                    code: ErrorCode.conflict,
                })
            } else {
                throw new CustomError({
                    code: ErrorCode.systemError,
                })
            }
        }
    }

    async getTenantEmployees(): Promise<TenantEmployees> {
        try {
            const dto: IGetTenantEmployeesDto = await ApiService.request({
                serviceName: ApiServiceName.user,
                followLink: {
                    entity: ApiLinkEntity.tenantSettingsEmployees,
                    rel: ApiLinkRel.all,
                },
            })
            return new TenantEmployees(dto)
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async updateTenantEmployees(link: IApiLink, data: IUpdateTenantEmployeesDto): Promise<TenantEmployees> {
        try {
            const dto: IGetTenantEmployeesDto = await ApiService.followLink({
                link,
                data,
            })
            return new TenantEmployees(dto)
        } catch (e) {
            if (e.isAxiosError && e.response.status === 409) {
                throw new CustomError({
                    code: ErrorCode.conflict,
                })
            } else {
                throw new CustomError({
                    code: ErrorCode.systemError,
                })
            }
        }
    }

    async getTenantInvoicePositions(): Promise<TenantInvoicePositions> {
        try {
            const dto: IGetTenantInvoicePositionsDto = await ApiService.request({
                serviceName: ApiServiceName.user,
                followLink: {
                    entity: ApiLinkEntity.tenantSettingsInvoicePosition,
                    rel: ApiLinkRel.all,
                },
            })
            return new TenantInvoicePositions(dto)
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async createTenantInvoicePosition(link: IApiLink, data: ICreateTenantInvoicePositionDto): Promise<void> {
        try {
            await ApiService.followLink({
                link, data: {
                    ...data,
                    description: prepareTranslations(data.description),
                } as ICreateTenantInvoicePositionDto
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async updateTenantInvoicePosition(link: IApiLink, data: IUpdateTenantInvoicePositionDto): Promise<void> {
        try {
            await ApiService.followLink({
                link, data: {
                    ...data,
                    description: prepareTranslations(data.description),
                } as IUpdateTenantInvoicePositionDto
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getOrderTypes(): Promise<OrderTypes> {
        try {
            const dto: IGetOrderTypesDto = await ApiService.request({
                serviceName: ApiServiceName.user,
                followLink: {
                    entity: ApiLinkEntity.tenantSettingsOrderType,
                    rel: ApiLinkRel.all,
                },
            })
            return new OrderTypes(dto)
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async createOrderType(link: IApiLink, data: ICreateOrderTypeDto): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data,
            })
        } catch (e) {
            if (e.isAxiosError && e.response.status === 409) {
                throw new CustomError({
                    code: ErrorCode.conflict,
                })
            } else {
                throw new CustomError({
                    code: ErrorCode.systemError,
                })
            }
        }
    }

    async getPaymentTerms(): Promise<PaymentTerm[]> {
        try {
            const resp: { entities: IGetPaymentTermDto[] } = await ApiService.request({
                serviceName: ApiServiceName.user,
                followLink: {
                    entity: ApiLinkEntity.tenantSettingsTermOfPayment,
                    rel: ApiLinkRel.all,
                },
            })
            return resp.entities.map(dto => new PaymentTerm(dto))
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async getTermsAndConditions(): Promise<TermsAndConditions> {
        try {
            const dto: IGetTermsAndConditionsDto = await ApiService.request({
                serviceName: ApiServiceName.user,
                followLink: {
                    entity: ApiLinkEntity.tenantSettingsTermsAndConditions,
                    rel: ApiLinkRel.all,
                },
            })
            return new TermsAndConditions(dto)
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async updateTermsAndConditions(link: IApiLink, data: IUpdateTermsAndConditions): Promise<TermsAndConditions> {
        try {
            const dto: IGetTermsAndConditionsDto = await ApiService.followLink({
                link,
                data: {
                    ...data,
                    termsAndConditions: prepareTranslations(data.termsAndConditions),
                } as IUpdateTermsAndConditions,
            })
            return new TermsAndConditions(dto)
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getDeliveryTerms(): Promise<DeliveryTerms> {
        try {
            const dto: IGetDeliveryTermsDto = await ApiService.request({
                serviceName: ApiServiceName.user,
                followLink: {
                    entity: ApiLinkEntity.tenantSettingsDeliveryTerms,
                    rel: ApiLinkRel.all,
                },
            })
            return new DeliveryTerms(dto)
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getDeliveryTermByLink(link: IApiLink): Promise<DeliveryTerm> {
        try {
            const dto: IGetDeliveryTermDto = await ApiService.followLink({ link })
            return new DeliveryTerm(dto)
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async createUpdateDeliveryTerm(link: IApiLink, data: ICreateUpdateDeliveryTermDto): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data: {
                    ...data,
                    termDescription: prepareTranslations(data.termDescription),
                } as ICreateUpdateDeliveryTermDto
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getTextBuildingBlocks(params: IGetTextBuilidngBlockParams): Promise<IMultipleEntities<TextBuildingBlock, { linkCreate?: IApiLink }>> {
        try {
            const data: IMultipleEntitiesResponse<IGetTextBuildingBlockDto> = await ApiService.request({
                serviceName: ApiServiceName.user,
                followLink: {
                    entity: ApiLinkEntity.tenantSettingsTextBuildingBlock,
                    rel: ApiLinkRel.all,
                    query: {
                        Criteria: params.search,
                        Scope: params.scope,
                        PageSize: params.pageSize,
                        PageNumber: params.pageNumber,
                        NotFormattedOnly: params.notFormattedOnly,
                    }
                },
            })
            return {
                list: data.entities.map(tbb => new TextBuildingBlock(tbb)),
                pagination: data.paginationMetadata,
                loadMore: hasMorePages(data.paginationMetadata) ? () => this.getTextBuildingBlocks({ ...params, pageNumber: data.paginationMetadata!.currentPage + 1 }) : undefined,
                aggregates: {
                    linkCreate: data.links?.find(lnk => lnk.entity === ApiLinkEntity.tenantSettingsTextBuildingBlock && lnk.rel === ApiLinkRel.new)
                }
            }
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async createTextBuildingBlock(link: IApiLink, data: ICreateTextBuildingBlockDto): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data,
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async updateTextBuildingBlock(link: IApiLink, data: IUpdateTextBuildingBlockDto): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data,
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async deleteTextBuildingBlock(link: IApiLink): Promise<void> {
        try {
            await ApiService.followLink({ link })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    /**
     * Loads the companies the current person has access to with this app, excluding the company of the current
     * login. Uses a fixed path because the user service root links are unreachable for users the gateway tenant
     * check rejects. Not cached, since roles can change between visits.
     * @returns the accessible companies, sorted by name
     */
    async getAccessibleCompanies(): Promise<AccessibleCompany[]> {
        const data: { companies: IAccessibleCompanyDto[] } = await ApiService.requestServicePath({
            serviceName: ApiServiceName.user,
            path: "Companies/Accessible",
        })
        return data.companies.map(AccessibleCompany.fromDTO)
    }

    async getCompanies(): Promise<Company[]> {
        if (this.#companies !== null) {
            return this.#companies
        }
        try {
            const data: { companies: { tenantId: Guid, name: string }[] } = await ApiService.request({
                serviceName: ApiServiceName.user,
                followLink: {
                    entity: ApiLinkEntity.company,
                    rel: ApiLinkRel.all,
                },
            })
            const companies = data.companies.map(c => new Company({
                id: c.tenantId,
                name: c.name,
            }))
            this.#companies = companies
            return companies
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async getCountries({ onlyWithInternalCode }: { onlyWithInternalCode?: boolean } = {}): Promise<Country[]> {
        if (onlyWithInternalCode) {
            if (this.#countriesWithInternalCode
                && this.#countriesWithInternalCodeLanguage
                && this.#countriesWithInternalCodeLanguage === i18n.language) {
                return this.#countriesWithInternalCode
            }
        } else {
            if (this.#countries
                && this.#countriesLanguage
                && this.#countriesLanguage === i18n.language) {
                return this.#countries
            }
        }

        try {
            const resp: { entities: IGetCountryDto[] } = await ApiService.request({
                serviceName: ApiServiceName.user,
                followLink: {
                    entity: ApiLinkEntity.tenantCountries,
                    rel: ApiLinkRel.all,
                    query: {
                        onlyWithInternalCode,
                    }
                },
            })
            const countries = resp.entities.map(dto => new Country(dto))
            if (onlyWithInternalCode) {
                this.#countriesWithInternalCode = countries
                this.#countriesWithInternalCodeLanguage = i18n.language || null
            } else {
                this.#countries = countries
                this.#countriesLanguage = i18n.language || null
            }
            return countries
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async getCountryIsoNumericCode(params: {
        countryName?: string
        isoAlpha2Code?: string
    }): Promise<string | null> {
        if (!params.countryName && !params.isoAlpha2Code) {
            return null
        }
        try {
            const resp: { isoNumericCode: string } = await ApiService.request({
                serviceName: ApiServiceName.user,
                followLink: {
                    entity: ApiLinkEntity.tenantCountryIsoNumericCode,
                    rel: ApiLinkRel.self,
                    query: params,
                },
            })
            return resp.isoNumericCode
        } catch (e) {
            return null
        }
    }

    async getUserRoles(): Promise<UserRoles> {
        try {
            const dto: IGetUserRolesItemDto[] = await ApiService.request({
                serviceName: ApiServiceName.user,
                followLink: {
                    entity: ApiLinkEntity.userRoles,
                    rel: ApiLinkRel.all,
                },
            })
            return new UserRoles(dto)
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async updateUserRoles(data: { userId: Guid, roles: Role[] }[]): Promise<void> {
        try {
            await ApiService.request({
                serviceName: ApiServiceName.user,
                followLink: {
                    entity: ApiLinkEntity.userRoles,
                    rel: ApiLinkRel.update,

                },
                data,
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getTenantSettingsGlobalAssetOverview(): Promise<TenantSettingsGlobalAssetOverview> {
        try {
            const dto: IGetTenantSettingsGlobalAssetOverviewDto = await ApiService.request({
                serviceName: ApiServiceName.user,
                followLink: {
                    entity: ApiLinkEntity.tenantSettingsGlobalAssetOverview,
                    rel: ApiLinkRel.all,
                },
            })
            return new TenantSettingsGlobalAssetOverview(dto)
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async updateTenantSettingsGlobalAssetOverview(link: IApiLink, data: IUpdateTenantSettingsGlobalAssetOverviewDto): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data,
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getGlobalAssetOverviewTenants({ includeCurrentTenant }: { includeCurrentTenant?: boolean } = {}): Promise<GlobalAssetOverviewTenant[]> {
        try {
            const dto: IGetGlobalAssetOverviewTenantDto[] = await ApiService.request({
                serviceName: ApiServiceName.user,
                followLink: {
                    entity: ApiLinkEntity.tenantGlobalAssetOverview,
                    rel: ApiLinkRel.all,
                    query: { includeCurrentTenant }
                },
            })
            return dto.map(t => new GlobalAssetOverviewTenant(t))
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

}

export default new UserService()
